import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography, Radio, RadioGroup, FormControlLabel } from '@mui/material';

import {
  gray100,
  primaryButtonBackgroundColor,
  gray40,
  white,
  orange50,
  gray80,
} from '../../../constants/colors';
import { contactSchema } from '../../../validation/contact';
import type { ContactFormData, ContactCategory } from '../../../types/contact';
import type { CompanyData } from '../../../types/company';

import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import MetaTitle from '../../utils/MetaTitle';
import SideBar from '../../uiParts/menu/SideBar';
import { useProxyAccessControl } from '../../../hooks/useProxyAccessControl';
import { getCompany } from '../../../api/firebase/firestore/company/read';
import { getUser } from '../../../api/firebase/firestore/user/read';
import { contactCategory } from '../../../constants/enum';
import {
  generateContactTemplate,
  getTemplateTypeFromParams,
} from '../../../utils/contact/templateGenerator';
import { usePageLayout } from '../../../hooks/usePageLayout';
import type { UserType } from '../../../types/user';

const styles = {
  pageContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '32px 16px 16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  modalContainer: {
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    padding: '40px',
    width: '100%',
    maxWidth: '480px',
    boxSizing: 'border-box',
    position: 'relative',
    '@media screen and (max-width: 960px)': {
      padding: '32px 16px',
    },
  },
  modalHeader: {
    marginBottom: '24px',
    '@media screen and (max-width: 960px)': {
      marginBottom: '16px',
    },
  },
  modalTitle: {
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    textAlign: 'left',
    '@media screen and (max-width: 960px)': {
      fontSize: '20px',
    },
  },
  infoSection: {
    marginBottom: '24px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: '16px',
    '@media screen and (max-width: 960px)': {
      flexDirection: 'column',
      marginBottom: '12px',
    },
  },
  infoLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: gray100,
    minWidth: '140px',
    marginRight: '16px',
    '@media screen and (max-width: 960px)': {
      minWidth: 'auto',
      marginBottom: '4px',
    },
  },
  infoValue: {
    fontSize: '14px',
    color: gray100,
    flex: 1,
  },
  requiredChip: {
    color: orange50,
    fontSize: '12px',
    fontWeight: '500',
    marginLeft: '8px',
  },
  fieldLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: gray100,
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
  },
  userInfoFieldLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: gray80,
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
  },
  radioGroup: {
    marginBottom: '24px',
  },
  textArea: {
    width: '100%',
    marginBottom: '24px',
  },
  submitButton: {
    width: '100%',
    height: '44px',
    fontSize: '16px',
    fontWeight: '700',
    marginTop: '8px',
  },
};

const Contact: React.FC = (): JSX.Element => {
  const [errorText, setErrorText] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const { user, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  // 代理モード中はアクセス禁止
  useProxyAccessControl();

  // 確認画面から戻ってきた場合の入力内容を取得
  const savedFormData = location.state?.formData as ContactFormData | undefined;
  const savedCompanyData = location.state?.companyData as CompanyData | undefined;

  // URLパラメータから初期値を取得
  const categoryParam = searchParams.get('category');
  const typeParam = searchParams.get('type');

  // カテゴリのマッピング（categoryパラメータから判定）
  const getCategoryFromParam = (param: string | null): ContactCategory => {
    if (param === 'account') return contactCategory.account as ContactCategory;
    if (param === 'plan') return contactCategory.plan as ContactCategory;
    if (param === 'system') return contactCategory.system as ContactCategory;
    return contactCategory.other as ContactCategory;
  };

  // カテゴリIDからURLパラメータに変換する関数
  const getCategoryParam = (category: number): string => {
    switch (category) {
      case contactCategory.account:
        return 'account';
      case contactCategory.plan:
        return 'plan';
      case contactCategory.system:
        return 'system';
      case contactCategory.other:
        return 'other';
      default:
        return 'other';
    }
  };

  // 初期カテゴリ値
  const initialCategory = getCategoryFromParam(categoryParam);

  // 編集可能判定（カテゴリが99（その他）の場合のみ編集可能）
  const isEditable = initialCategory === contactCategory.other;

  // 企業招待では電話番号を任意登録にしているため（#3071）、
  // 企業に電話番号が未登録の場合は問い合わせ時に入力できるようにする（#3236）
  const isPhoneNumberEditable = isEditable || !companyData?.phone_number;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: yupResolver(contactSchema),
    defaultValues: {
      companyName: '',
      department: '',
      name: '',
      email: '',
      phoneNumber: '',
      category: initialCategory,
      message: '',
    },
  });

  const onSubmit = (data: ContactFormData): void => {
    // 確認画面へ遷移（状態を保持）
    const categoryParam = getCategoryParam(data.category);
    navigate(`/contact/confirm?category=${categoryParam}`, {
      state: { formData: data, companyData },
    });
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (isLoading) return;

      if (user === null) {
        return;
      }

      try {
        setLoading(true);

        // 確認画面から戻ってきた場合は保存されたデータを復元
        if (savedFormData) {
          setValue('companyName', savedFormData.companyName);
          setValue('department', savedFormData.department);
          setValue('name', savedFormData.name);
          setValue('email', savedFormData.email);
          setValue('phoneNumber', savedFormData.phoneNumber);
          setValue('category', savedFormData.category);
          setValue('message', savedFormData.message);

          if (savedCompanyData) {
            setCompanyData(savedCompanyData);
          }
        } else if (!isEditable) {
          // カテゴリが99（その他）以外の場合のみユーザー情報を自動入力
          // ユーザー情報を取得
          const fetchedUser = await getUser(user.uid);
          if (fetchedUser) {
            const userDataTyped = fetchedUser as UserType;
            setValue('name', userDataTyped.name || '');
            setValue('email', userDataTyped.email || '');
            setValue('department', userDataTyped.department || '');
          }

          // 企業情報を取得
          if (user.company_id) {
            const company = await getCompany(user.company_id);
            if (company) {
              const companyDataTyped = company as CompanyData;
              setCompanyData(companyDataTyped);
              setValue('companyName', companyDataTyped.company_name || '');
              setValue('phoneNumber', companyDataTyped.phone_number || '');

              // テンプレートタイプを取得してテンプレートを生成
              const templateType = getTemplateTypeFromParams(
                categoryParam ?? undefined,
                typeParam ?? undefined,
              );
              const template = generateContactTemplate(templateType, companyDataTyped);
              setValue('message', template);
            }
          }
        }
      } catch (error: unknown) {
        setErrorText('データの取得に失敗しました。');
        setOpen(true);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [
    user,
    isLoading,
    navigate,
    categoryParam,
    typeParam,
    setValue,
    isEditable,
    savedFormData,
    savedCompanyData,
  ]);

  if (loading || isLoading) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
        <Box sx={styles.pageContainer}>
          <Box sx={styles.modalContainer}>
            <Box sx={styles.modalHeader}>
              <Typography sx={styles.modalTitle}>お問い合わせ</Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              {/* 問い合わせ者情報 */}
              <Box sx={styles.infoSection}>
                {/* 会社名 */}
                <Controller
                  name="companyName"
                  control={control}
                  render={({ field }): JSX.Element => (
                    <CustomTextfield
                      {...field}
                      labelText="会社名"
                      requiredText="必須"
                      error={Boolean(errors.companyName)}
                      helperText={errors.companyName?.message}
                      disabled={!isEditable}
                      width="100%"
                      inputWidth="100%"
                      formMb="24px"
                    />
                  )}
                />

                {/* 部署名 */}
                <Controller
                  name="department"
                  control={control}
                  render={({ field }): JSX.Element => (
                    <CustomTextfield
                      {...field}
                      labelText="部署名"
                      error={Boolean(errors.department)}
                      helperText={errors.department?.message}
                      disabled={!isEditable}
                      width="100%"
                      inputWidth="100%"
                      formMb="24px"
                    />
                  )}
                />

                {/* 担当者名 */}
                <Controller
                  name="name"
                  control={control}
                  render={({ field }): JSX.Element => (
                    <CustomTextfield
                      {...field}
                      labelText="担当者名"
                      requiredText="必須"
                      error={Boolean(errors.name)}
                      helperText={errors.name?.message}
                      disabled={!isEditable}
                      width="100%"
                      inputWidth="100%"
                      formMb="24px"
                    />
                  )}
                />

                {/* メールアドレス */}
                <Controller
                  name="email"
                  control={control}
                  render={({ field }): JSX.Element => (
                    <CustomTextfield
                      {...field}
                      labelText="メールアドレス"
                      requiredText="必須"
                      type="email"
                      error={Boolean(errors.email)}
                      helperText={errors.email?.message}
                      disabled={!isEditable}
                      width="100%"
                      inputWidth="100%"
                      formMb="24px"
                    />
                  )}
                />

                {/* 電話番号 */}
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }): JSX.Element => (
                    <CustomTextfield
                      {...field}
                      labelText="電話番号"
                      requiredText="必須"
                      error={Boolean(errors.phoneNumber)}
                      helperText={errors.phoneNumber?.message}
                      disabled={!isPhoneNumberEditable}
                      width="100%"
                      inputWidth="100%"
                      formMb="24px"
                    />
                  )}
                />
              </Box>

              {/* お問い合わせの種類 */}
              <Box sx={styles.radioGroup}>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }): JSX.Element => (
                    <Box>
                      <Typography sx={styles.fieldLabel}>
                        お問い合わせの種類
                        <Typography component="span" sx={styles.requiredChip}>
                          必須
                        </Typography>
                      </Typography>
                      <RadioGroup
                        value={field.value}
                        onChange={(e): void => field.onChange(Number(e.target.value))}
                      >
                        <FormControlLabel
                          value={contactCategory.account}
                          control={
                            <Radio sx={{ color: gray40, '&.Mui-checked': { color: orange50 } }} />
                          }
                          label="アカウントに関すること"
                        />
                        <FormControlLabel
                          value={contactCategory.plan}
                          control={
                            <Radio sx={{ color: gray40, '&.Mui-checked': { color: orange50 } }} />
                          }
                          label="料金プランに関すること"
                        />
                        <FormControlLabel
                          value={contactCategory.system}
                          control={
                            <Radio sx={{ color: gray40, '&.Mui-checked': { color: orange50 } }} />
                          }
                          label="システムに関すること"
                        />
                        <FormControlLabel
                          value={contactCategory.other}
                          control={
                            <Radio sx={{ color: gray40, '&.Mui-checked': { color: orange50 } }} />
                          }
                          label="その他"
                        />
                      </RadioGroup>
                      {errors.category && (
                        <Typography sx={{ color: orange50, fontSize: '12px', marginTop: '4px' }}>
                          {errors.category.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </Box>

              {/* お問い合わせ内容 */}
              <Controller
                name="message"
                control={control}
                render={({ field }): JSX.Element => (
                  <CustomTextfield
                    {...field}
                    labelText="お問い合わせ内容"
                    requiredText="必須"
                    multiline
                    rows={10}
                    placeholder="お問い合わせ内容をご記入下さい"
                    error={Boolean(errors.message)}
                    helperText={errors.message?.message}
                    width="100%"
                    inputWidth="100%"
                    height="auto"
                    formMb="24px"
                  />
                )}
              />

              <Button
                type="submit"
                variant="contained"
                sx={{
                  ...styles.submitButton,
                  backgroundColor: primaryButtonBackgroundColor,
                  '&:hover': {
                    backgroundColor: primaryButtonBackgroundColor,
                    opacity: 0.8,
                  },
                }}
              >
                確認画面へ
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <CustomSnackbar open={open} onClose={(): void => setOpen(false)} text={errorText} />
    </Box>
  );
};

export default Contact;
